import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Auth } from 'aws-amplify';
import * as yup from 'yup';
import Button from '@material-ui/core/Button';
import { TextField, Box } from '@material-ui/core';

/* Styles */
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import cn from 'classnames';

/* i18n */
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';

/* Material UI and other UI Dependencies */
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import Password from './Password';
import { passwordTests } from 'lib/utils';

function SignUp ({
  setAuthState,
  setUser,
  setUsername,
  setPassword
}) {
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const history = useHistory();

  const validationSchema = yup.object({
      password: yup
        .string()
        .matches(passwordTests['lowercase'], intl.formatMessage({id:'forms.signUp.passwordRequirementLowercase'}))
        .matches(passwordTests['uppercase'], intl.formatMessage({id:'forms.signUp.passwordRequirementUppercase'}))
        .matches(passwordTests['special'], intl.formatMessage({id:'forms.signUp.passwordRequirementSpecialCharacter'}))
        .matches(passwordTests['digit'], intl.formatMessage({id:'forms.signUp.passwordRequirementNumber'}))
        .min(passwordTests['length'], intl.formatMessage({id:'forms.signUp.passwordRequirementMinLength'}))
        .matches(passwordTests['nospaces'], intl.formatMessage({id:'forms.signUp.passwordRequirementNoSpaces'}))
        .required(intl.formatMessage({id:'forms.signIn.passwordRequired', description:'string'})),
      username: yup
        .string()
        .matches(/^\S+@\S+\.\w\w+$/, intl.formatMessage({id:'forms.signIn.matchEmail', description:'string'}))
        .required(intl.formatMessage({id:'forms.signIn.usernameRequired', description:'string'}))
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: ''
    },
    validationSchema: validationSchema,
    onSubmit: async ({ username, password }) => {
      trackEvent('CREATEACCOUNT_SIGNUP');
      setLoginError('');
      setSubmitting(true);
      setUsername(username);
      try {
        await Auth.signUp({username, password});
        setPassword(password);
        history.push(`${urlPrefix}/login/verify`);
      } catch (err) {
        setPassword('');
        setLoginError(err.message);
        setUser(null);
        setAuthState(null);
      }
      setSubmitting(false);
    },
  });

  const haveAccountClick = () => {
    trackEvent('CREATEACCOUNT_SIGNIN');
    history.push(`${urlPrefix}/login`);
  }

  const render = () => {
    if (submitting) {
      return (
        <>
          <Typography align="center" variant="h1" gutterBottom>
            <FormattedMessage id="forms.signUp.creatingAccount" />
          </Typography>
          <hr className={layoutStyles.separatorBold} />
          <div className={styles.loadingContainer}>
            <Box m={2} />
            <CircularProgress />
            <Box m={1} />
          </div>
        </>
      );
    } else {
      return (
        <>
          <Typography align="center" variant="h1" gutterBottom>
            <FormattedMessage default="Sign in to your account" id="forms.signUp.title" />
          </Typography>
          <Typography align="center" variant="body1" gutterBottom>
            <FormattedMessage id="forms.signUp.subtitle" />
          </Typography>
          <Typography align="center" variant="body1" gutterBottom>
            <span>
              <FormattedMessage id="forms.signUp.haveAccount" />
            </span>
            {' '}
            <span className={layoutStyles.link} style={{whiteSpace: 'nowrap'}} onClick={haveAccountClick}>
              <FormattedMessage id="forms.signUp.signIn" />
            </span>
          </Typography>
          <hr className={layoutStyles.separatorBold} />
          <Box pb={1}>
            <form onSubmit={formik.handleSubmit} className={cn(styles.loginFlow__form)}>
              <Typography variant="h5" component="h2" gutterBottom>
                <FormattedMessage id="forms.signUp.formTitle" />
              </Typography>
              <Box mt={3}>
                <TextField
                  FormHelperTextProps={{
                    className: styles.helperText
                  }}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  fullWidth
                  helperText={formik.touched.username && formik.errors.username}
                  label={intl.formatMessage({id:'forms.signIn.username', description:'string'})}
                  name="username"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder={intl.formatMessage({id:'forms.signIn.enterUsername', description:'string'})}
                  type="email"
                  value={formik.values.username}
                  variant="filled"
                  inputProps={{
                    title: intl.formatMessage({id:'forms.signIn.username'})
                  }}
                />
              </Box>
              <Password
                error={loginError}
                value={formik.values.password}
                formikTouched={formik.touched.password}
                formikError={formik.errors.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className={styles.submitBtn__container}>
                <Button
                  color="secondary"
                  disabled={submitting}
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  { submitting ? (
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">
                        <FormattedMessage id="forms.signIn.loading" />
                      </span>
                    </div>
                  ) : (
                    <FormattedMessage id="forms.signUp.signUp" />
                  )}
                </Button>
              </div>
            </form>
          </Box>
        </>
      );
    }
  };

  return(
    <div className={styles.loginFlow__form}>
      {render()}
    </div>
  );
}

SignUp.propTypes = {
  setAuthState: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  setUsername: PropTypes.func.isRequired,
  setPassword: PropTypes.func.isRequired,
}

export default SignUp;